<template>
  <div>
    <GmapMap
      ref="gmap"
      class="google-map"
      :center="mapCenter"
      :zoom="6"
      :options="mapOptions"
      style="width: 100%; height: 380px"
      @tilesloaded="addCustomControl"
    >
      <!-- Existing Route Markers -->
      <template v-if="currentPlaybackIndex == 0">
        <gmap-custom-marker
          v-for="(v, i) in routes"
          :key="i"
          :marker="{
            lat: parseFloat(v.latitude),
            lng: parseFloat(v.longitude),
          }"
        >
          <!-- <template v-if="currentPlaybackIndex > 0">
            <div
              v-if="!routes[i + 1]"
              class="pin-icon-container"
              @click="showInfo(v)"
            >
              <div
                class="pin-circle"
                :class="vehicleStatus"
              >
                <Icons :name="`marker-${vehicleBodyType}`" />
              </div>
              <div class="pin-arrow" />
            </div>
          </template> -->
          <template v-if="i === 0 || !routes[i + 1]">
            <div
              class="pin-icon-container"
              @click="showInfo(v)"
            >
              <div
                class="pin-circle"
                :class="vehicleStatus"
              >
                <Icons
                  v-if="i === 0"
                  :name="`marker-${vehicleBodyType}`"
                />
                <v-icon v-else>
                  mdi-flag-checkered
                </v-icon>
              </div>
              <div class="pin-arrow" />
            </div>
          </template>
          <template v-else>
            <div :class="getColor(v)">
              <v-icon small>
                mdi-circle
              </v-icon>
            </div>
          </template>
        </gmap-custom-marker>
      </template>

      <!-- Animated Marker -->
      <gmap-custom-marker
        v-else
        :marker="animatedMarkerPosition"
      >
        <div class="pin-icon-container">
          <div
            class="pin-circle"
            :class="vehicleStatus"
          >
            <Icons :name="`marker-${vehicleBodyType}`" />
          </div>
          <div class="pin-arrow" />
        </div>
      </gmap-custom-marker>

      <!-- Info Window -->
      <GmapInfoWindow
        :position="infoWindowPosition"
        :opened="infoWindowOpened"
        @closeclick="infoWindowOpened = false"
      >
        <div class="info-window">
          <div
            v-for="r in popupOptions"
            :key="r"
          >
            <span>{{ r.replace(/_/g, " ") }}:</span>
            <span>{{ infoWindowContent[r] }}</span>
          </div>
        </div>
      </GmapInfoWindow>

      <!-- Path -->
      <GmapPolyline
        :path="path"
        :options="polylineOptions"
      />
    </GmapMap>

    <!-- Playback Controls -->
    <div
      ref="customControl"
      class="playback-controls custom-map-control"
    >
      <select
        v-model="playbackInterval"
        :disabled="isPlaying"
      >
        <option :value="1000">
          1x
        </option>
        <option :value="500">
          2x
        </option>
        <option :value="250">
          4x
        </option>
        <option :value="150">
          5x
        </option>
      </select>
      <button @click="togglePlayback">
        <v-icon>{{ isPlaying ? "mdi-pause" : "mdi-play" }}</v-icon>
      </button>
    </div>
  </div>
</template>

<script>
import GmapCustomMarker from "vue2-gmap-custom-marker";

export default {
  name: "GoogleMapPlayback",
  components: {
    GmapCustomMarker,
    Icons: () => import("@/components/base/icons.vue"),
  },
  props: {
    tripsData: {
      type: Array,
      default: () => [],
    },
    directionArrowColor: {
      type: Boolean,
      default: false,
    },
    popupOptions: {
      type: Array,
      default: () => ["latitude", "longitude", "date_time"],
    },
    vehicleStatus: {
      type: String,
      default: "",
    },
    vehicleBodyType: {
      type: String,
      default: "",
    },
    eventData: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      mapCenter: { lat: 30.3753, lng: 69.3451 },
      mapOptions: {
        fullscreenControl: true,
        fullscreenControlOptions: {
          position: window.google?.maps.ControlPosition.RIGHT_BOTTOM,
        },
      },
      polylineOptions: {
        strokeColor: "#20a390",
        strokeOpacity: 1.0,
        strokeWeight: 4,
      },
      infoWindowOpened: false,
      infoWindowPosition: null,
      infoWindowContent: "",
      isPlaying: false,
      playbackInterval: 500,
      currentPlaybackIndex: 0,
      routes: this.tripsData,
      animatedMarkerPosition: null,
      animationFrameId: null,
    };
  },
  computed: {
    path() {
      return this.tripsData.map((p) => ({
        lat: parseFloat(p.latitude),
        lng: parseFloat(p.longitude),
      }));
    },
  },
  mounted() {
    this.initMap();
  },
  beforeDestroy() {
    cancelAnimationFrame(this.animationFrameId);
  },
  methods: {
    initMap() {
      this.$refs.gmap.$mapPromise.then(() => {
        if (window.google && window.google.maps) {
          this.mapOptions.fullscreenControlOptions = {
            position: window.google.maps.ControlPosition.BOTTOM_RIGHT,
          };
        }
        this.setMapBounds();
      });
    },
    setMapBounds() {
      if (!this.path.length) return;
      const bounds = new window.google.maps.LatLngBounds();
      this.path.forEach((coord) => {
        bounds.extend(new window.google.maps.LatLng(coord.lat, coord.lng));
      });
      this.$refs.gmap.$mapObject.fitBounds(bounds);
    },
    getColor(v) {
      return v.over_speed ? "over-speed" : "normal-speed";
    },
    showInfo(v) {
      this.infoWindowPosition = {
        lat: parseFloat(v.latitude),
        lng: parseFloat(v.longitude),
      };
      this.infoWindowContent = v;
      this.infoWindowOpened = true;
    },
    togglePlayback() {
      this.isPlaying = !this.isPlaying;
      if (this.isPlaying) {
        this.startPlayback();
      } else {
        this.stopPlayback("pause");
      }
    },
    startPlayback() {
      if (this.currentPlaybackIndex === 0) {
        this.routes = [];
      }

      const animate = (start, end, duration = this.playbackInterval) => {
        const startTime = performance.now();

        const step = (currentTime) => {
          const progress = Math.min((currentTime - startTime) / duration, 1);
          const lat = start.lat + (end.lat - start.lat) * progress;
          const lng = start.lng + (end.lng - start.lng) * progress;
          this.animatedMarkerPosition = { lat, lng };
          this.mapCenter = { lat, lng };

          if (progress < 1) {
            this.animationFrameId = requestAnimationFrame(step);
          } else {
            this.routes.push(this.tripsData[this.currentPlaybackIndex]);
            this.currentPlaybackIndex++;
            this.runNextAnimation();
          }
        };

        this.animationFrameId = requestAnimationFrame(step);
      };

      this.runNextAnimation = () => {
        if (this.currentPlaybackIndex >= this.tripsData.length - 1) {
          this.stopPlayback();
          return;
        }

        const current = this.tripsData[this.currentPlaybackIndex];
        const next = this.tripsData[this.currentPlaybackIndex + 1];
        animate(
          {
            lat: parseFloat(current.latitude),
            lng: parseFloat(current.longitude),
          },
          {
            lat: parseFloat(next.latitude),
            lng: parseFloat(next.longitude),
          }
        );
      };

      this.runNextAnimation();
    },
    stopPlayback(v) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
      this.isPlaying = false;
      if (v !== "pause") {
        this.animatedMarkerPosition = null;
        this.currentPlaybackIndex = 0;
        // this.routes = [];
      }
    },
    addCustomControl() {
      const map = this.$refs.gmap.$mapObject;
      const controlDiv = this.$refs.customControl;

      // Only add if not already added
      if (!this._customControlAdded) {
        map.controls[window.google.maps.ControlPosition.TOP_RIGHT].push(
          controlDiv
        );
        this._customControlAdded = true;
      }
    },
  },
};
</script>

<style scoped>
.google-map {
  width: 100%;
  height: 380px;
}
.playback-controls {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 5;
  display: flex;
  align-items: center;
  gap: 10px;
}
.playback-controls button,
.playback-controls select {
  padding: 4px 10px;
  border-radius: 6px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
  background: white;
  border: 1px solid #ccc;
  font-size: 14px;
}
.pin-icon-container {
  position: relative;
  width: 40px;
  height: 60px;
}
.pin-circle {
  width: 40px;
  height: 40px;
  padding: 5px;
  background-color: #fff;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
}
.pin-arrow {
  position: absolute;
  top: 40px;
  left: 15px;
  width: 10px;
  height: 20px;
  background-color: #ffffff;
  clip-path: polygon(50% 0%, 0% 100%, 100% 100%);
}
.normal-speed,
.over-speed {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #20a390;
  box-shadow: 0 0 3px rgba(0, 0, 0, 0.3);
}
.normal-speed i {
  color: #20a390;
}
.over-speed i {
  color: #f07870;
}
.info-window div {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  text-transform: capitalize;
}
:deep .active svg path {
  fill: #23bdaa !important;
}
:deep .in_active svg path,
:deep .inactive svg path {
  fill: #ffae20 !important;
}
:deep .offline svg path {
  fill: #fa896b;
}
</style>
